import { useQuery } from '@tanstack/react-query'
import {
  Chip,
  CircularProgress,
  Dialog, DialogContent, DialogTitle,
  IconButton,
  Table, TableBody, TableCell, TableHead, TableRow,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import dayjs from 'dayjs'

import { governanceApi } from '../services/governance-api'

interface IProps {
  workspaceId: string
  open: boolean
  onClose(): void
}

const URGENCY_COLOR: Record<string, 'default' | 'warning' | 'success'> = {
  vitalicia: 'default',
  ativa: 'success',
  expirando_30d: 'warning',
}

/**
 * K10 (active exceptions) drill-down dialog. Pattern B — full PII.
 */
export function ExceptionsDrilldownDialog({ workspaceId, open, onClose }: IProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['governance', 'exceptions-drilldown', workspaceId],
    queryFn: () => governanceApi.exceptionsDrilldown({ workspaceId, limit: 200 }),
    enabled: open && !!workspaceId,
  })

  const rows = data?.data.rows ?? []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div">Exceções ativas (K10)</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <CircularProgress size={20} />}
        {isError && <Typography color="error">Erro ao carregar exceções.</Typography>}
        {data && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>CPF</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Plano</TableCell>
                <TableCell>Vence em</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.excecao_id}>
                  <TableCell>{r.person_tax_payer_id}</TableCell>
                  <TableCell>{r.person_name ?? '—'}</TableCell>
                  <TableCell>{r.person_type ?? '—'}</TableCell>
                  <TableCell>{r.plan ?? '—'}</TableCell>
                  <TableCell>{r.expire_date ? dayjs(r.expire_date).format('DD/MM/YYYY') : 'Vitalícia'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.urgency === 'expirando_30d' ? 'Expira em 30d' : r.urgency}
                      color={URGENCY_COLOR[r.urgency] ?? 'default'}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center">Nenhuma exceção ativa.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  )
}
